import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { ProfileService } from '../../services/profile.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import {
  hhmmToTotalMinutes,
  parseOperatingTimeToHHmm,
  snapHHmmToHalfHour,
} from '../../shared/utils/operating-hours-time.util';
import {
  HOURS_UPDATE_FIELDS,
  buildLocationUpdatePayload,
} from '../../shared/utils/location-details.util';

/**
 * Component for displaying and editing a location's weekly operating hours with utilities to copy hours from other locations, validate time ranges, and persist changes to the back-end.
 * @component
 * @selector app-operating-hours
 * @example
 *   <app-operating-hours [locationDetails]="location" [locations]="allLocations" [startEditable]="false" (cancelClicked)="onCancel()" (locationUpdated)="onLocationUpdated($event)"></app-operating-hours>
 * @input any locationDetails - Full details of the currently selected location (expected to include a hours array). Used to populate the editable days/times.
 * @input any[] locations - List of available locations used to copy/apply operating hours from another location.
 * @input boolean startEditable - When true, the component will open in edit mode on init.
 * @output EventEmitter<void> cancelClicked - Emitted when editing is cancelled or after a successful save to notify the parent to close/refresh UI.
 * @output EventEmitter<any> locationUpdated - Emitted after successfully saving and refetching location details; payload contains updated location data.
 *
 * Key methods:
 * - onEdit(): Enable editing mode and snapshot original hours for cancellation.
 * - onSave(): Build payload from current day state, call ProfileService to update the location, refetch, update local state, emit locationUpdated and notify parent.
 * - onCancel(): Revert to original hours snapshot, reset editing state and emit cancelClicked.
 * - applyHours(): Fetch selected location's hours and apply them to the current days (used for copying hours between locations).
 * - updateHours(hours): Apply raw hours array to the component's days model and trigger modification check.
 * - onLocationChange(event): Handle selection of a different location for copying hours.
 * - onOpenTimeChange(day), onTimeChange(), onToggleDay(day): UI handlers that validate times, reset/initialize times, and mark the form modified as needed.
 * - getFilteredCloseTimes(day): Return a list of valid close times based on the day's open time to prevent invalid ranges.
 * - compareTimes(time1, time2): Compare two 12-hour format time strings by converting to minutes.
 *
 * Important private helpers:
 * - mapHoursToDays(hours): Normalize API hours array to the component's days structure (name, isOpen, openTime, closeTime).
 * - formatTime(time), formatTimeToHHMM(time): Convert and normalize time strings to consistent 12-hour display/HH:MM AM/PM formats for API and UI.
 * - generateTimes(): Generate a 24-hour set of half-hour increment time strings used by select controls.
 * - timeToMinutes(time): Convert a 12-hour time string to minutes since midnight for comparisons.
 * - checkIfFormModified(): Compare current days to original snapshot to determine if the form has unsaved changes.
 *
 * Lifecycle hooks:
 * - constructor(profileService, messageService): Initializes time options and injects services used for API calls and user messages.
 * - ngOnInit(): Opens editor on load if startEditable is true.
 * - ngOnChanges(changes): Watches for changes to locationDetails and remaps hours into the internal days model while storing the original snapshot for cancellation detection.
 */
@Component({
  selector: 'app-operating-hours',
  standalone: true,
  imports: [CommonModule, MatSelectModule, FormsModule],
  templateUrl: './operating-hours.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./operating-hours.component.scss'],
})
export class OperatingHoursComponent implements OnChanges, OnInit {
  @Input() locationDetails: any;
  @Input() locations: any[] = [];
  @Input() startEditable = false;
  times: string[] = [];

  isEditing = false;
  isFormModified = false;
  selectedLocation: any;
  buttonText = 'Apply Hours';

  @Output() cancelClicked = new EventEmitter<void>();
  @Output() locationUpdated = new EventEmitter<any>();
  days: { name: string; isOpen: boolean; openTime: string | null; closeTime: string | null }[] = [];

  // Store original operating hours data for cancellation
  private originalDaysData: any[] = [];

  constructor(
    private profileService: ProfileService,
    private messageService: NotificationService
  ) {
    this.times = this.generateTimes();
  }

  ngOnInit(): void {
    if (this.startEditable) {
      this.onEdit();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locationDetails'] && this.locationDetails && this.locationDetails.hours) {
      this.days = this.mapHoursToDays(this.locationDetails.hours);
      this.originalDaysData = JSON.parse(JSON.stringify(this.days));
    }
  }

  /**
   * Map an array of raw hourly entries to a 7-day ordered array with formatted open/close times.
   * @example
   * mapHoursToDays([{ day: 'monday', is_closed: false, open_time: '09:00', close_time: '17:00' }])
   * [
   *   { name: 'sunday', isOpen: false, openTime: null, closeTime: null },
   *   { name: 'monday', isOpen: true, openTime: '09:00', closeTime: '17:00' },
   *   { name: 'tuesday', isOpen: false, openTime: null, closeTime: null },
   *   { name: 'wednesday', isOpen: false, openTime: null, closeTime: null },
   *   { name: 'thursday', isOpen: false, openTime: null, closeTime: null },
   *   { name: 'friday', isOpen: false, openTime: null, closeTime: null },
   *   { name: 'saturday', isOpen: false, openTime: null, closeTime: null }
   * ]
   * @param {{any[]}} {{hours}} - Array of hour objects which may contain day, is_closed, open_time, and close_time fields.
   * @returns {{{ name: string; isOpen: boolean; openTime: string | null; closeTime: string | null }[]}} Ordered array of seven day objects with name, isOpen and formatted open/close times.
   **/
  private mapHoursToDays(
    hours: any[]
  ): { name: string; isOpen: boolean; openTime: string | null; closeTime: string | null }[] {
    const daysOrder = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    return daysOrder.map(dayName => {
      const hourData = hours.find((h: any) => String(h.day).toLowerCase() === dayName);
      if (hourData) {
        return {
          name: hourData.day,
          isOpen: !hourData.is_closed,
          openTime: hourData.is_closed ? null : this.matchTimeOptionFromApi(hourData.open_time),
          closeTime: hourData.is_closed ? null : this.matchTimeOptionFromApi(hourData.close_time),
        };
      }
      return {
        name: dayName,
        isOpen: false,
        openTime: null,
        closeTime: null,
      };
    });
  }

  /**
   * Convert a time string to 12-hour "h:mm AM/PM" format and ensure AM/PM is uppercase, or return null for falsy input.
   * @example
   * formatTimeToHHMM('14:30')
   * 2:30 PM
   * @param {{string|null}} {{time}} - Time string in 24-hour form (e.g., "14:30") or 12-hour form (e.g., "2:30pm"), or null.
   * @returns {{string|null}} Formatted time in "h:mm AM/PM" with uppercase AM/PM, or null if input is falsy.
   **/
  private formatTimeToHHMM(time: string | null): string | null {
    if (!time) return null;
    // Ensure time has AM/PM in uppercase format
    // If it already has AM/PM, ensure it's uppercase
    if (time.match(/\s*(AM|PM|am|pm)/i)) {
      return time.replace(/\s*(AM|PM|am|pm)/i, match => ' ' + match.trim().toUpperCase());
    }
    // If no AM/PM found, convert from 24-hour format to 12-hour format with AM/PM
    const date = new Date(`1970-01-01 ${time}`);
    const timeString = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return timeString.replace(/\s*(AM|PM)/i, match => ` ${match.toUpperCase()}`);
  }

  private formatTime(time: string): string | null {
    if (!time) {
      return null;
    }
    const date = new Date(`1970-01-01 ${time}`);
    return date
      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      .replace(/\s*(AM|PM)/i, match => match.toUpperCase());
  }

  onLocationChange(event: any) {
    this.selectedLocation = event.value;
    this.buttonText = 'Apply Hours';
  }

  /**
   * Applies operating hours from the currently selected location by fetching location details and updating the component state; sets the apply button text to 'Applied' on success and logs errors on failure.
   * @example
   * pplyHours()
   * undefined
   * @returns {{void}} No return value.
   **/
  applyHours() {
    if (this.selectedLocation && this.selectedLocation.id) {
      this.profileService.getLocationDetails(this.selectedLocation.id).subscribe({
        next: response => {
          const { data } = response;
          const hours = data.hours;
          if (hours) {
            this.updateHours(hours);
            this.buttonText = 'Applied';
          }
        },
        error: err => {
          console.error('Failed to get location details', err);
        },
      });
    }
  }

  /**
   * Update the component's days using the provided hours array, setting isOpen and formatted open/close times.
   * @example
   * pdateHours([{ day: 'monday', is_closed: false, open_time: '09:00', close_time: '17:00' }])
   * undefined
   * @param {{Array.<{day: string, is_closed: boolean, open_time: string|null, close_time: string|null}>}} {{hours}} - Array of hour objects containing day, is_closed, open_time and close_time.
   * @returns {{void}} Nothing is returned.
   **/
  updateHours(hours: any[]) {
    this.days.forEach(day => {
      const hourData = hours.find(h => h.day.toLowerCase() === day.name.toLowerCase());
      if (hourData) {
        day.isOpen = !hourData.is_closed;
        day.openTime = hourData.is_closed ? null : this.matchTimeOptionFromApi(hourData.open_time);
        day.closeTime = hourData.is_closed
          ? null
          : this.matchTimeOptionFromApi(hourData.close_time);
      }
    });

    this.checkIfFormModified();
  }

  /** Map API time string to an exact entry from {@link times} so p-select shows the selection. */
  private matchTimeOptionFromApi(raw: string | null | undefined): string | null {
    const hhmm = snapHHmmToHalfHour(parseOperatingTimeToHHmm(raw));
    if (!hhmm) return null;
    const target = hhmmToTotalMinutes(hhmm);
    const exact = this.times.find(t => this.timeToMinutes(t) === target);
    if (exact) return exact;
    let best: string | null = null;
    let bestDiff = Infinity;
    for (const t of this.times) {
      const d = Math.abs(this.timeToMinutes(t) - target);
      if (d < bestDiff) {
        bestDiff = d;
        best = t;
      }
    }
    return best;
  }

  private generateTimes(): string[] {
    const times: string[] = [];
    for (let i = 0; i < 24; i += 1) {
      for (let j = 0; j < 60; j += 30) {
        const date = new Date(2000, 0, 1, i, j);
        const timeString = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        times.push(timeString.replace(/\s*(AM|PM)/i, match => match.toUpperCase()));
      }
    }
    return times;
  }

  getFilteredCloseTimes(day: any): string[] {
    if (!day.isOpen || !day.openTime) {
      return this.times;
    }

    // Filter out times that are before or equal to the open time
    return this.times.filter(time => {
      return this.compareTimes(time, day.openTime) > 0;
    });
  }

  compareTimes(time1: string, time2: string): number {
    // Convert time strings to comparable format
    const time1Minutes = this.timeToMinutes(time1);
    const time2Minutes = this.timeToMinutes(time2);

    return time1Minutes - time2Minutes;
  }

  /**
   * Convert a 12-hour clock time string (e.g., "8:00 AM") to total minutes since midnight.
   * @example
   * timeToMinutes("8:00 AM")
   * 480
   * @param {{string}} {{time}} - Time string in "h:mm AM|PM" format.
   * @returns {{number}} Total minutes since midnight for the given time.
   **/
  private timeToMinutes(time: string): number {
    // Convert time string like "8:00 AM" to total minutes
    const [timePart, period] = time.split(' ');
    const [hours, minutes] = timePart.split(':').map(Number);

    let totalMinutes = hours * 60 + minutes;

    const periodUpper = period?.toUpperCase();
    if (periodUpper === 'PM' && hours !== 12) {
      totalMinutes += 12 * 60; // Add 12 hours for PM times (except 12 PM)
    } else if (periodUpper === 'AM' && hours === 12) {
      totalMinutes -= 12 * 60; // Subtract 12 hours for 12 AM
    }

    return totalMinutes;
  }

  onOpenTimeChange(day: any): void {
    // If close time is before or equal to open time, clear it
    if (day.closeTime && day.openTime && this.compareTimes(day.closeTime, day.openTime) <= 0) {
      day.closeTime = null;
    }

    this.checkIfFormModified();
  }
  onEdit() {
    this.isEditing = true;
    this.isFormModified = false; // Reset modification tracking
    // Store current days data as backup before enabling editing
    this.originalDaysData = JSON.parse(JSON.stringify(this.days));
  }

  /**
   * Persist updated operating hours to the backend for the current location, refresh location details, update local day data, emit updates to parent components, and show success/error notifications.
   * @example
   * nSave()
   * undefined
   * @returns {void} Performs asynchronous updates and side effects; does not return a value.
   **/
  onSave() {
    if (!this.locationDetails) return;

    const updatedHours = this.days.map(day => {
      const isClosed = !day.isOpen;
      const hourData: any = {
        day: day.name,
        is_closed: isClosed,
      };

      // Only include open_time and close_time if the day is not closed
      if (!isClosed) {
        hourData.open_time = this.formatTimeToHHMM(day.openTime);
        hourData.close_time = this.formatTimeToHHMM(day.closeTime);
      }

      return hourData;
    });

    const payload = buildLocationUpdatePayload(
      { hours: updatedHours },
      { allowedFields: HOURS_UPDATE_FIELDS }
    );

    this.profileService.updateLocationDetails(this.locationDetails.id, payload).subscribe({
      next: () => {
        // Refetch location details from API to get the latest data
        this.profileService.getLocationDetails(this.locationDetails.id).subscribe({
          next: response => {
            if (response.status && response.data) {
              // Update location details with fresh data from API
              const updatedLocationDetails = response.data;

              // Update days data with fresh hours from API
              if (updatedLocationDetails.hours) {
                this.days = this.mapHoursToDays(updatedLocationDetails.hours);
              }

              // Update original data after successful save
              this.originalDaysData = JSON.parse(JSON.stringify(this.days));

              // Emit updated location details to parent component
              this.locationUpdated.emit(updatedLocationDetails);

              // Reset all modification tracking
              this.isEditing = false;
              this.isFormModified = false;

              this.cancelClicked.emit();
              this.messageService.add({
                severity: 'success',
                summary: 'Changes Saved!',
                detail: ' Your changes have been saved successfully',
              });
            } else {
              // If refetch fails, still update local state
              this.originalDaysData = JSON.parse(JSON.stringify(this.days));
              this.isEditing = false;
              this.isFormModified = false;

              this.cancelClicked.emit();
              this.messageService.add({
                severity: 'success',
                summary: 'Changes Saved!',
                detail: ' Your changes have been saved successfully',
              });
            }
          },
          error: refetchError => {
            console.error('Error refetching location details', refetchError);
            // Still update local state even if refetch fails
            this.originalDaysData = JSON.parse(JSON.stringify(this.days));
            this.isEditing = false;
            this.isFormModified = false;

            this.cancelClicked.emit();
            this.messageService.add({
              severity: 'success',
              summary: 'Changes Saved!',
              detail: ' Your changes have been saved successfully',
            });
          },
        });
      },
      error: error => {
        console.error('Error updating location details', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update operating hours. Please try again.',
        });
      },
    });
  }

  onCancel() {
    this.isEditing = false;
    this.isFormModified = false;
    // Restore original operating hours data
    this.days = JSON.parse(JSON.stringify(this.originalDaysData));
    this.selectedLocation = null; // Clear any selected location for copying hours
    this.cancelClicked.emit();
  }

  /**
   * Toggle a day's open/closed state and set default opening and closing times when opened.
   * @example
   * nToggleDay({ isOpen: true })
   * undefined
   * @param {any} day - Day object containing an isOpen boolean and optional openTime/closeTime properties.
   * @returns {void} No return value.
   **/
  onToggleDay(day: any): void {
    if (!day.isOpen) {
      day.openTime = null;
      day.closeTime = null;
    } else {
      day.openTime = '8:00 AM';
      day.closeTime = '6:00 PM';
    }

    this.checkIfFormModified();
  }

  onTimeChange(): void {
    this.checkIfFormModified();
  }

  private checkIfFormModified(): void {
    const currentData = JSON.stringify(this.days);
    const originalData = JSON.stringify(this.originalDaysData);
    this.isFormModified = currentData !== originalData;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
